import { useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { tFn } from '../i18n';
import { findRejoinableGames, updateAllProfileSettings } from '../utils/supabaseActions';
import { hasBotSuffix } from '../utils/botUtils';
import { DEFAULT_THEME_KEY } from '../theme';
import { mapAuthError } from '../utils/errorMapper';

/**
 * useAuth — installs the session-restore effect and returns all authentication handlers.
 *
 * Design: App.js keeps all `useState` declarations for auth state (currentUser, screen,
 * login form fields, etc.) and passes the setters in. This avoids the circular-dependency
 * problem that arises when moving `currentUser` ownership into a hook that itself depends
 * on callbacks from hooks that receive `currentUser` (useSettings, usePresence).
 *
 * Circular deps (clearPresence, resetInitializing, setOnlinePlayers) are provided via
 * `authCallbacksRef` — a mutable ref that App.js populates AFTER all hooks have been
 * called, guaranteeing they are up-to-date before any user gesture can trigger logout.
 *
 * @param {Object} params
 * @param {Function} params.setCurrentUser
 * @param {Function} params.setScreen
 * @param {string}   params.loginEmail        - current value (read by login/handlePasswordReset)
 * @param {string}   params.loginPassword     - current value (read by login)
 * @param {string}   params.signupUsername    - current value (read by login)
 * @param {boolean}  params.isSignup          - current value (read by login)
 * @param {string}   params.newPassword       - current value (read by handleSetNewPassword)
 * @param {Function} params.setLoginEmail
 * @param {Function} params.setLoginPassword
 * @param {Function} params.setSignupUsername
 * @param {Function} params.setIsSignup
 * @param {Function} params.setIsResetPassword
 * @param {Function} params.setLoginError
 * @param {Function} params.setResetMessage
 * @param {Function} params.setNewPassword
 * @param {Function} params.setRejoinableGames
 * @param {Function} params.loadSettingsFromProfile - from useSettings
 * @param {Function} params.finishInitializing      - from useSettings
 * @param {Function} params.setLanguageAndPersist   - from useSettings (used as dep anchor)
 * @param {string}   params.language                - current UI language for error messages
 * @param {string}   params.themeKey                - current theme key to persist on signup/guest
 * @param {React.MutableRefObject} params.authCallbacksRef
 *   Mutable ref populated by App.js with { clearPresence, resetInitializing, setOnlinePlayers }.
 *   Written synchronously after all hooks run, so it is always valid before any user gesture.
 *
 * @returns {{ login, loginAsGuest, upgradeGuestAccount, logout, handlePasswordReset, handleSetNewPassword }}
 */
export function useAuth({
  setCurrentUser,
  setScreen,
  loginEmail,
  loginPassword,
  signupUsername,
  isSignup,
  newPassword,
  guestUsername,
  setLoginEmail,
  setLoginPassword,
  setSignupUsername,
  setIsSignup,
  setIsResetPassword: _setIsResetPassword,
  setLoginError,
  setResetMessage,
  setNewPassword,
  setGuestUsername,
  setRejoinableGames,
  setIsGuest,
  loadSettingsFromProfile,
  finishInitializing,
  setLanguageAndPersist,
  language,
  themeKey = DEFAULT_THEME_KEY,
  authCallbacksRef,
}) {
  const t = (key) => tFn(language || 'en', key);

  // ── Session restore + auth state changes ─────────────────────────────────────
  // Mount-only: re-running on dep changes would cause re-auth loops.
  // `setLanguageAndPersist` is included as dep anchor only — it is stable (useCallback)
  // so the effect never actually re-runs; the linter requires a dep for closure refs.
  useEffect(() => {
    const fetchProfileWithRetry = async (email, retries = 3, delayMs = 800) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('username')
          .eq('email', email)
          .single();
        if (profile?.username) return profile;
        if (attempt < retries) {
          console.warn(`Profile not found for ${email}, retrying (${attempt}/${retries})...`);
          await new Promise(r => setTimeout(r, delayMs));
        } else {
          console.warn(`Profile not found for ${email} after ${retries} attempts`, error);
        }
      }
      return null;
    };

    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const isAnonymous = session.user.is_anonymous === true;

          // Anonymous (guest) users have no email — look up profile by auth_uid
          let profile;
          if (isAnonymous) {
            const { data: p } = await supabase
              .from('profiles')
              .select('username, is_guest, guest_expires_at')
              .eq('auth_uid', session.user.id)
              .single();
            profile = p;

            // Check if guest has expired
            if (profile?.guest_expires_at && new Date(profile.guest_expires_at) < new Date()) {
              console.warn('Guest session expired — signing out');
              await supabase.auth.signOut().catch(() => {});
              finishInitializing();
              return;
            }
          } else {
            profile = await fetchProfileWithRetry(session.user.email);
          }

          // If the session exists but no profile is found (orphaned / stale session),
          // sign out so the user can do a clean sign-in. Without this the app hangs
          // on the login screen showing no error, and then the manual sign-in also
          // fails because Supabase still holds the stale session while re-Auth races.
          if (!profile?.username) {
            console.warn('Session found but profile missing — signing out orphaned session');
            await supabase.auth.signOut().catch(() => {});
            finishInitializing();
            return;
          }

          if (profile?.username) {
            console.log(`Restoring session for user: ${profile.username}`);
            setCurrentUser(profile.username);
            setIsGuest(!!profile.is_guest);

            // Non-anonymous users must confirm email before full access
            if (!isAnonymous && !session.user.email_confirmed_at) {
              setScreen('verify-email');
              finishInitializing();
              return;
            }

            // Load user settings from database
            await loadSettingsFromProfile(profile.username);

            // Load rejoin games
            const activeGames = await findRejoinableGames(profile.username);
            setRejoinableGames(activeGames);

            // Navigate based on rejoinable games
            if (activeGames.length > 0) {
              setScreen('rejoin-or-new');
            } else {
              setScreen('create-or-join');
            }

            // Mark initialization as complete — allows useEffects to save user changes
            finishInitializing();
          }
        }
      } catch (err) {
        console.error('Failed to restore session:', err);
      }
    };

    // Detect recovery redirect BEFORE checkSession runs.
    // The hash still contains the token at this point — Supabase will read it
    // via onAuthStateChange. We just set a flag so checkSession won't navigate
    // away from the reset-password screen.
    const isRecovery = window.location.hash.includes('type=recovery');

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setScreen('reset-password');
        window.history.replaceState(null, '', window.location.pathname);
      } else if (event === 'SIGNED_OUT') {
        // Supabase can fire SIGNED_OUT briefly during auto-token-refresh on slow
        // networks (local dev, 192.168.x.x). Delay before acting so a follow-up
        // TOKEN_REFRESHED can arrive and restore the session.
        // For intentional logouts, logout() already sets screen='login' synchronously,
        // so this handler becomes a no-op in that path.
        setTimeout(() => {
          supabase.auth.getSession().then(({ data: { session } }) => {
            if (!session?.user) {
              setCurrentUser(null);
              setScreen('login');
            }
          });
        }, 2000);
      }
    });

    if (isRecovery) {
      // Don't call checkSession — it would find the recovery session and
      // redirect to lobby, overwriting the reset-password screen.
      setScreen('reset-password');
    } else {
      checkSession();
    }

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setLanguageAndPersist]);

  // ── Auth handlers ─────────────────────────────────────────────────────────────

  const login = async () => {
    const email = loginEmail.trim();
    const password = loginPassword.trim();
    const username_input = signupUsername.trim();

    if (!email || !password) {
      setLoginError(t('errEmailPassword'));
      return;
    }

    if (isSignup && !username_input) {
      setLoginError(t('errEnterUsername'));
      return;
    }

    if (isSignup && hasBotSuffix(username_input)) {
      setLoginError(t('errBotSuffix'));
      return;
    }

    // SEC-1: enforce 3–20 alphanumeric / underscore characters
    if (isSignup && !/^[a-zA-Z0-9_]{3,20}$/.test(username_input)) {
      setLoginError(t('errUsernameFormat'));
      return;
    }

    setLoginError('');

    try {
      if (isSignup) {
        // Sign up -- pass username as metadata so the DB trigger can create the profile
        // without needing an active session (email confirmation is typically required)
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { username: username_input } }
        });

        if (error) throw error;

        if (data?.user) {
          // Profile creation is handled server-side by the handle_new_user() trigger
          // on auth.users. We only do a client-side insert as a fallback for cases
          // where the trigger is not deployed (e.g. local dev without the migration).
          const { error: insertError } = await supabase
            .from('profiles')
            .insert({
              username: username_input,
              email: email,
              wins: 0,
              games_played: 0,
              total_score: 0
            });
          // insertError is expected when the trigger already created the profile,
          // or when email confirmation is pending (no session → RLS rejects insert).
          // Both cases are safe to ignore here; the trigger is the source of truth.
          if (insertError) {
            console.warn('Client-side profile insert skipped (trigger handles it):', insertError.message);
          }

          setCurrentUser(username_input);
          setLoginPassword('');
          setSignupUsername('');
          setIsSignup(false);

          // Persist the language the user chose on the login screen as their default
          updateAllProfileSettings(username_input, {
            viewMode: '3d', preferredColorIndex: 0, showSymbols: false, showReactions: true, chatSoundsEnabled: false, language, themeKey
          }).catch(() => {});

          // After signup, email is not yet confirmed — show verification screen
          if (!data.user.email_confirmed_at) {
            setScreen('verify-email');
          } else {
            setScreen('create-or-join');
          }
        }
      } else {
        // Sign in
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (error) throw error;

        if (data?.user) {
          // Get username from profiles.
          // Retry up to 5 times: Chrome/PWA can have a brief window where the JWT is
          // propagated to cookies but Supabase RLS hasn't seen it yet, or the DB
          // read-replica lags slightly after a fresh sign-up. A 200ms head-start
          // delay lets the auth layer settle before the first query.
          await new Promise(r => setTimeout(r, 200));
          let profile = null;
          for (let attempt = 1; attempt <= 5; attempt++) {
            const { data: p, error: profileError } = await supabase
              .from('profiles')
              .select('username')
              .eq('email', email)
              .single();
            if (p?.username) { profile = p; break; }
            if (attempt < 5) {
              console.warn(`Profile not found on login attempt ${attempt}/5, retrying...`);
              await new Promise(r => setTimeout(r, 800));
            } else {
              console.error('Profile lookup failed after 5 attempts', profileError);
              throw new Error(t('errProfileNotFound'));
            }
          }

          setCurrentUser(profile.username);
          setLoginPassword('');

          // Check email confirmation before allowing full access
          if (!data.user.email_confirmed_at) {
            setScreen('verify-email');
            return;
          }

          // Load user settings
          await loadSettingsFromProfile(profile.username);

          // Check for active games to rejoin
          const activeGames = await findRejoinableGames(profile.username);
          setRejoinableGames(activeGames);

          if (activeGames.length > 0) {
            setScreen('rejoin-or-new');
          } else {
            setScreen('create-or-join');
          }
        }
      }
    } catch (e) {
      console.error('Auth error:', e);
      setLoginError(mapAuthError(e, t));
    }
  };

  const logout = async () => {
    try {
      await authCallbacksRef.current.clearPresence?.();
      await supabase.auth.signOut();
    } catch (e) {
      console.error('Logout error:', e);
    }

    setCurrentUser(null);
    setIsGuest(false);
    setLoginEmail('');
    setLoginPassword('');
    setSignupUsername('');
    setGuestUsername('');
    setLoginError('');
    setRejoinableGames([]);
    setIsSignup(false);
    setScreen('login');
    authCallbacksRef.current.setOnlinePlayers?.([]);
    authCallbacksRef.current.resetInitializing?.();
  };

  // ── Guest (anonymous) auth ──────────────────────────────────────────────────

  const loginAsGuest = async () => {
    const username_input = guestUsername.trim();

    if (!username_input) {
      setLoginError(t('errEnterUsername'));
      return;
    }

    if (hasBotSuffix(username_input)) {
      setLoginError(t('errBotSuffix'));
      return;
    }

    if (!/^[a-zA-Z0-9_]{3,20}$/.test(username_input)) {
      setLoginError(t('errUsernameFormat'));
      return;
    }

    setLoginError('');

    try {
      const { data, error } = await supabase.auth.signInAnonymously();
      if (error) throw error;

      if (!data?.user) throw new Error('Anonymous sign-in failed');

      // Create guest profile via RPC (trigger skips anonymous users)
      const { error: rpcError } = await supabase.rpc('create_guest_profile', {
        p_username: username_input,
      });

      if (rpcError) {
        // Clean up the anonymous auth session on failure
        await supabase.auth.signOut().catch(() => {});
        if (rpcError.message?.includes('USERNAME_TAKEN')) {
          throw new Error('USERNAME_TAKEN');
        }
        throw rpcError;
      }

      setCurrentUser(username_input);
      setIsGuest(true);
      setGuestUsername('');
      setLoginError('');

      // Persist the language chosen on the login screen
      updateAllProfileSettings(username_input, {
        viewMode: '3d', preferredColorIndex: 0, showSymbols: false, showReactions: true, chatSoundsEnabled: false, language, themeKey
      }).catch(() => {});

      setScreen('create-or-join');
    } catch (e) {
      console.error('Guest login error:', e);
      setLoginError(mapAuthError(e, t));
    }
  };

  const upgradeGuestAccount = async (email, password) => {
    if (!email || !password) return { error: t('errEmailPassword') };
    if (password.length < 6) return { error: t('errPasswordMin') };

    try {
      // Update the auth user with email + password (sends confirmation email)
      const { error } = await supabase.auth.updateUser({ email, password });
      if (error) throw error;

      // Update the profile server-side
      const { error: rpcError } = await supabase.rpc('upgrade_guest_account', {
        p_email: email,
      });

      if (rpcError) {
        if (rpcError.message?.includes('EMAIL_TAKEN')) {
          return { error: t('errUsernameExists') };
        }
        throw rpcError;
      }

      setIsGuest(false);
      return { success: true };
    } catch (e) {
      console.error('Upgrade error:', e);
      return { error: mapAuthError(e, t) };
    }
  };

  const handlePasswordReset = async () => {
    const email = loginEmail.trim();
    if (!email) {
      setLoginError(t('errEnterEmail'));
      return;
    }
    setLoginError('');
    setResetMessage('');
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin
      });
      if (error) throw error;
      setResetMessage(t('errResetSent'));
    } catch (e) {
      setLoginError(mapAuthError(e, t, 'errPasswordReset'));
    }
  };

  const handleSetNewPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setLoginError(t('errPasswordMin'));
      return;
    }
    setLoginError('');
    setResetMessage('');
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setResetMessage(t('errPasswordUpdated'));
      setNewPassword('');
      setTimeout(() => {
        setScreen('login');
        setResetMessage('');
      }, 2500);
    } catch (e) {
      setLoginError(mapAuthError(e, t, 'errPasswordUpdate'));
    }
  };

  return { login, loginAsGuest, upgradeGuestAccount, logout, handlePasswordReset, handleSetNewPassword };
}
